import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { trackEvent } from '../../analytics';
import { buildReturnToPath } from '../../navigation/authRedirect';
import styles from './AuthRequiredModal.module.css';

const AUTH_MESSAGE = 'Create an account or sign in to react, comment, save favorites, and build your RideSpot presence.';

interface AuthRequiredModalProps {
  onClose: () => void;
}

export function AuthRequiredModal({ onClose }: AuthRequiredModalProps) {
  const location = useLocation();
  const previousOverflowRef = useRef<string>('');
  const initialLocationKeyRef = useRef(location.key);
  const returnTo = buildReturnToPath(location);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    previousOverflowRef.current = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = previousOverflowRef.current;
    };
  }, [onClose]);

  useEffect(() => {
    trackEvent('auth_modal_seen', { path: location.pathname });
  }, [location.pathname]);

  useEffect(() => {
    if (location.key !== initialLocationKeyRef.current) {
      onClose();
    }
  }, [location.key, onClose]);

  const modal = (
    <div
      className={styles.backdrop}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-modal-title"
    >
      <div className={styles.card} onClick={(e) => e.stopPropagation()}>
        <p id="auth-modal-title" className={styles.message}>{AUTH_MESSAGE}</p>
        <div className={styles.actions}>
          <Link
            to="/create-account"
            className={styles.createAccountBtn}
            onClick={() => {
              trackEvent('cta_click', { context: 'auth_modal', label: 'create_account' });
              onClose();
            }}
          >
            Create account
          </Link>
          <Link
            to="/login"
            state={{ returnTo }}
            className={styles.signInBtn}
            onClick={() => {
              trackEvent('cta_click', { context: 'auth_modal', label: 'sign_in' });
              onClose();
            }}
          >
            Sign in
          </Link>
          <button
            type="button"
            className={styles.okBtn}
            onClick={() => {
              trackEvent('cta_click', { context: 'auth_modal', label: 'dismiss' });
              onClose();
            }}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
